/**
 * Plate maths.
 *
 * "Load 97.5 kg" is a number; what you actually need is "a 20, a 15, a 5 and a
 * 1.25 on each side". Doing that arithmetic between sets with your heart rate
 * at 160 is exactly the sort of thing a computer should be doing for you.
 */

export interface PlateCount {
  kg: number;
  count: number;
}

export interface PlateLoad {
  targetKg: number;
  barKg: number;
  /** What to hang on ONE side. Mirror it on the other. */
  perSide: PlateCount[];
  /** What the bar actually weighs once loaded as above. */
  achievableKg: number;
  /** Target minus achievable — non-zero when the plates can't make it exactly. */
  shortfallKg: number;
  /** False when the target is below the bar itself. */
  possible: boolean;
}

/** What a reasonably equipped commercial gym has, heaviest first. */
export const DEFAULT_PLATES_KG = [25, 20, 15, 10, 5, 2.5, 1.25];

/**
 * Greedy largest-first. That's optimal for the standard plate denominations
 * (each is at least double the next one down in the useful range), and when it
 * can't hit the number exactly it reports the shortfall rather than silently
 * rounding — so you find out on screen, not under the bar.
 */
export function platesFor(
  targetKg: number,
  barKg: number,
  available: number[] = DEFAULT_PLATES_KG,
): PlateLoad {
  const empty: PlateLoad = {
    targetKg,
    barKg,
    perSide: [],
    achievableKg: barKg,
    shortfallKg: targetKg - barKg,
    possible: targetKg >= barKg,
  };

  if (targetKg < barKg) return { ...empty, shortfallKg: 0 };
  if (targetKg === barKg) return { ...empty, shortfallKg: 0 };

  let perSideRemaining = (targetKg - barKg) / 2;
  const perSide: PlateCount[] = [];

  // Float arithmetic on 1.25s drifts; work to the nearest gram.
  const round = (n: number) => Math.round(n * 1000) / 1000;
  perSideRemaining = round(perSideRemaining);

  for (const plate of [...available].sort((a, b) => b - a)) {
    if (plate <= 0) continue;
    const count = Math.floor(round(perSideRemaining / plate));
    if (count > 0) {
      perSide.push({ kg: plate, count });
      perSideRemaining = round(perSideRemaining - plate * count);
    }
  }

  const loadedPerSide = perSide.reduce((sum, p) => sum + p.kg * p.count, 0);
  const achievableKg = round(barKg + loadedPerSide * 2);

  return {
    targetKg,
    barKg,
    perSide,
    achievableKg,
    shortfallKg: round(targetKg - achievableKg),
    possible: true,
  };
}

/** "20 + 15 + 5 + 1.25 per side" — compact enough for a set row. */
export function describeLoad(load: PlateLoad): string {
  if (!load.possible) return `Below the ${load.barKg} kg bar`;
  if (load.perSide.length === 0) return 'Empty bar';
  return load.perSide
    .flatMap((p) => Array.from({ length: p.count }, () => String(p.kg)))
    .join(' + ');
}

/** Bars you might pick up. Used by the calculator's bar selector. */
export const BAR_OPTIONS: { kg: number; name: string }[] = [
  { kg: 20, name: 'Olympic bar' },
  { kg: 15, name: "Women's bar" },
  { kg: 10, name: 'EZ / short bar' },
  { kg: 7, name: 'Light EZ bar' },
  { kg: 0, name: 'No bar (machine)' },
];

/**
 * Only barbell lifts have plates to calculate. Machines with a weight stack and
 * dumbbells don't, so the calculator shouldn't offer itself there.
 */
export function usesPlates(loadType: string): boolean {
  return loadType === 'barbell';
}
